import { Box, Chip, Stack, Typography } from '@mui/material'
import type { ReactNode } from 'react'

interface PageHeaderProps {
  title: string
  /** Kept for API compatibility; compact corporate header does not render descriptions. */
  subtitle?: string
  module?: string
  actions?: ReactNode
}

export default function PageHeader({ title, module, actions }: PageHeaderProps) {
  const hasActions = Boolean(actions)

  return (
    <Box
      sx={{
        mb: 0.75,
        px: { xs: 0.75, sm: 1 },
        py: { xs: 0.45, sm: 0.5 },
        borderRadius: 2,
        border: '1px solid rgba(11, 54, 51, 0.12)',
        background:
          'linear-gradient(135deg, rgba(255,255,255,0.94), rgba(255,249,244,0.88)), radial-gradient(circle at 8% 0%, rgba(244,223,156,0.36), transparent 34%)',
        boxShadow: '0 10px 28px -24px rgba(11, 54, 51, 0.55)',
      }}
    >
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        spacing={{ xs: 0.5, lg: 0.75 }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
      >
        {!hasActions ? (
          <Box sx={{ minWidth: 0, flexShrink: 0 }}>
            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minHeight: 28 }}>
              <Typography
                variant="h6"
                sx={{
                  color: 'primary.dark',
                  fontWeight: 900,
                  letterSpacing: '-0.025em',
                  lineHeight: 1.15,
                  textTransform: 'uppercase',
                  fontSize: { xs: '0.92rem', sm: '1rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                {title}
              </Typography>
              {module ? (
                <Chip
                  size="small"
                  label={module}
                  sx={{
                    height: 22,
                    borderRadius: 999,
                    bgcolor: 'rgba(244, 223, 156, 0.34)',
                    color: 'secondary.dark',
                    border: '1px solid rgba(218, 131, 60, 0.28)',
                    fontWeight: 900,
                    fontSize: '0.65rem',
                    letterSpacing: 0.5,
                  }}
                />
              ) : null}
            </Stack>
          </Box>
        ) : null}
        {actions ? (
          <Box
            sx={{
              display: 'flex',
              minWidth: 0,
              width: '100%',
              justifyContent: { xs: 'flex-start', lg: 'flex-end' },
              '& > *': {
                width: '100%',
              },
            }}
          >
            {actions}
          </Box>
        ) : null}
      </Stack>
    </Box>
  )
}
